<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { computed, onBeforeUnmount, ref } from 'vue'

import UiIcon from '@/components/common/UiIcon.vue'
import NavigationLink from '@/components/navigation/NavigationLink.vue'
import type { NavigationItem, NavigationType } from '@/models/navigation'

defineOptions({
  name: 'DesktopNavigationItem',
})

const props = withDefaults(defineProps<{
  item: NavigationItem
  navigationType: NavigationType
  depth?: number
}>(), {
  depth: 0,
})

const hasChildren = computed(() => props.item.children.length > 0)
const isTopLevel = computed(() => props.depth === 0)
const isExtended = computed(() => props.navigationType === 'extended')
const hasDropdown = computed(() => isTopLevel.value && hasChildren.value)
const isOpen = ref(false)
const itemRef = ref<HTMLElement | null>(null)
let closeTimer: number | null = null
const triggerId = computed(() => hasDropdown.value
  ? `header-navigation-${props.item.id}-trigger`
  : undefined,
)
const submenuId = computed(() => hasDropdown.value
  ? `header-navigation-${props.item.id}`
  : undefined,
)

function openDropdown() {
  clearCloseTimer()
  if (hasDropdown.value) isOpen.value = true
}

function closeDropdown() {
  clearCloseTimer()
  isOpen.value = false
}

function clearCloseTimer() {
  if (closeTimer === null) return

  window.clearTimeout(closeTimer)
  closeTimer = null
}

function handlePointerLeave() {
  const activeElement = document.activeElement

  if (activeElement instanceof Node && itemRef.value?.contains(activeElement)) return

  clearCloseTimer()
  closeTimer = window.setTimeout(() => {
    isOpen.value = false
    closeTimer = null
  }, 120)
}

function handleFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget

  if (nextTarget instanceof Node && itemRef.value?.contains(nextTarget)) return

  closeDropdown()
}

onBeforeUnmount(clearCloseTimer)
</script>

<template>
  <!-- The list item owns hover/focus state for its link and descendant submenu. -->
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <li
    ref="itemRef"
    :class="isTopLevel ? 'relative' : ''"
    @mouseenter="openDropdown"
    @mouseleave="handlePointerLeave"
    @focusin="openDropdown"
    @focusout="handleFocusOut"
  >
    <NavigationLink
      :item="item"
      :id="triggerId"
      :class="isTopLevel
        ? 'inline-flex items-center gap-2 rounded-md transition-colors duration-200 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
        : 'panel-option items-start'"
      :aria-controls="submenuId"
      :aria-haspopup="hasDropdown || undefined"
      :aria-expanded="hasDropdown ? isOpen : undefined"
      :tabindex="hasDropdown ? 0 : undefined"
    >
      <template v-if="isTopLevel">
        <span>{{ item.label }}</span>
        <ChevronDown
          v-if="hasDropdown"
          :size="18"
          aria-hidden="true"
          class="transition-transform duration-200"
          :class="isOpen ? 'rotate-180' : ''"
        />
      </template>
      <template v-else>
        <span
          v-if="isExtended && item.icon"
          class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-deep"
        >
          <UiIcon
            :name="item.icon"
            class="size-4"
          />
        </span>
        <span class="min-w-0">
          <span>{{ item.label }}</span>
          <span
            v-if="isExtended && item.description"
            class="mt-1 block max-w-64 text-xs font-normal leading-relaxed text-muted-foreground"
          >
            {{ item.description }}
          </span>
        </span>
      </template>
    </NavigationLink>

    <ul
      v-if="hasChildren"
      :id="submenuId"
      :class="isTopLevel
        ? [
            'panel-ui absolute right-0 top-full z-50 mt-2 p-1 transition duration-150',
            isOpen ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-1 opacity-0',
            isExtended ? 'grid w-[min(42rem,calc(100vw-3rem))] grid-cols-2 gap-1' : 'min-w-56 space-y-1',
          ]
        : 'ml-4 mt-1 space-y-1 border-l border-border pl-2'"
    >
      <DesktopNavigationItem
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        :navigation-type="navigationType"
        :depth="depth + 1"
      />
    </ul>
  </li>
</template>
